import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { phoneError, normalizePhone } from '../utils/phone';
import StatusBadge from '../components/StatusBadge';
import { DSButton, DSInput, DSRadio, DSTextarea, DSIcon } from '../design-system/primitives';
import PinGolfScore from '../components/PinGolfScore';
import EventCancelledBanner from '../components/EventCancelledBanner';
import { isEventCancelled } from '../utils/eventHelpers';
import './EventRSVP.css';
import { personName } from '../utils/personName';

const RESPONSE_OPTIONS = [
  { value: 'going', label: 'Going', icon: 'check', modifier: 'going' },
  { value: 'interested', label: 'Interested', icon: 'star', modifier: 'interested' },
  { value: 'not_going', label: 'Not Going', icon: 'x', modifier: 'not-going' }
];

function EventRSVP() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [pingolfLeaderboard, setPingolfLeaderboard] = useState([]);
  const [loadingPingolfLeaderboard, setLoadingPingolfLeaderboard] = useState(false);
  const [expandedTieBreaks, setExpandedTieBreaks] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [existingRsvp, setExistingRsvp] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    status: 'going',
    message: ''
  });

  useEffect(() => {
    checkAuthAndFetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => {
    if (event?.event_metadata?.pinball_format !== 'pingolf') {
      return undefined;
    }

    fetchPingolfLeaderboard();
    const intervalId = setInterval(() => {
      fetchPingolfLeaderboard();
    }, 15000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id, event?.event_metadata?.pinball_format]);

  const checkAuthAndFetchData = async () => {
    try {
      setLoading(true);
      
      // Check if user is logged in
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const userResponse = await apiService.getCurrentUser();
          setCurrentUser(userResponse.data);
          setIsLoggedIn(true);
          
          // Try to fetch existing RSVP
          try {
            const rsvpResponse = await apiService.get(`/events/${eventId}/my-rsvp`);
            setExistingRsvp(rsvpResponse.data);
            setFormData(prev => ({
              ...prev,
              status: rsvpResponse.data.status,
              message: rsvpResponse.data.message || ''
            }));
          } catch (rsvpErr) {
            // No existing RSVP, that's okay
            console.log('No existing RSVP found');
          }
        } catch (authErr) {
          // Token invalid or expired
          setIsLoggedIn(false);
          localStorage.removeItem('token');
        }
      }
      
      // Fetch event details
      await fetchEvent();
    } catch (err) {
      setError('Failed to load event');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvent = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/events/${eventId}`);
      setEvent(response.data);
      setError(null);
    } catch (err) {
      setError('Event not found');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPingolfLeaderboard = async () => {
    try {
      setLoadingPingolfLeaderboard(true);
      const response = await apiService.get(`/events/${eventId}/pingolf-leaderboard`);
      setPingolfLeaderboard(response.data || []);
    } catch (err) {
      console.error('Error fetching Pingolf leaderboard:', err);
      setPingolfLeaderboard([]);
    } finally {
      setLoadingPingolfLeaderboard(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleLoggedInSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      await apiService.put(`/events/${eventId}/rsvp`, {
        status: formData.status,
        message: formData.message
      });
      
      setSuccess(existingRsvp ? 'RSVP updated successfully!' : 'RSVP submitted successfully!');
      
      // Refresh RSVP data
      const rsvpResponse = await apiService.get(`/events/${eventId}/my-rsvp`);
      setExistingRsvp(rsvpResponse.data);
      
      // Redirect after 2 seconds
      setTimeout(() => {
        navigate('/events');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit RSVP');
      console.error('Error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGuestSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const phoneMessage = phoneError(formData.phone);
    if (phoneMessage) {
      setError(phoneMessage);
      return;
    }

    setSubmitting(true);

    try {
      const response = await apiService.post(`/events/${eventId}/rsvp`, {
        ...formData,
        phone: normalizePhone(formData.phone)
      });
      
      // Show success message and redirect
      navigate(`/events/${eventId}/rsvp-confirmation`, { 
        state: { 
          event, 
          accountCreated: response.data.accountCreated 
        } 
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit RSVP');
      console.error('Error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Both states live inside .rsvp-page so they pick up this screen's colours rather than
  // relying on whichever stylesheet happens to define a bare .loading or .error
  if (loading) return <div className="rsvp-page"><div className="loading">Loading event...</div></div>;
  if (!event) return <div className="rsvp-page"><div className="error">Event not found</div></div>;

  const eventDate = new Date(event.event_date);
  const isPast = eventDate < new Date();
  const hasMatchPlay = event.event_metadata?.matchplay_url;
  const isPingolfEvent = event.event_metadata?.pinball_format === 'pingolf';

  const renderPingolfLeaderboard = () => {
    if (!isPingolfEvent) {
      return null;
    }

    return (
      <div className="pingolf-leaderboard-section">
        <div className="pingolf-leaderboard-header">
          <h2>PinGolf Leaderboard</h2>
          <span>Auto-refreshing</span>
        </div>

        {loadingPingolfLeaderboard ? (
          <p>Loading leaderboard...</p>
        ) : pingolfLeaderboard.length === 0 ? (
          <p className="pingolf-leaderboard-empty">No submitted cards yet.</p>
        ) : (
          <div className="pingolf-leaderboard-list">
            {pingolfLeaderboard.map((row) => {
              const displayName = personName(row) || 'Player';
              const rowKey = `${row.user_id}-${row.card_id}`;
              const isExpanded = !!expandedTieBreaks[rowKey];

              return (
                <div key={rowKey} className="pingolf-leaderboard-row">
                  <div className="pingolf-leaderboard-main">
                    <div className="pingolf-place">#{row.place}</div>
                    <div className="pingolf-player">
                      <strong>{displayName}</strong>
                      <small>Total: <PinGolfScore value={row.total_score} /></small>
                    </div>
                    <button
                      type="button"
                      className="pingolf-tiebreak-toggle"
                      onClick={() => {
                        setExpandedTieBreaks(prev => ({
                          ...prev,
                          [rowKey]: !prev[rowKey]
                        }));
                      }}
                    >
                      {isExpanded ? 'Hide Tie-break' : 'Tie-break'}
                    </button>
                  </div>
                  {isExpanded && (
                    <div className="pingolf-tiebreak-detail">
                      <p>{row.tie_break?.comparison_order}</p>
                      <p>
                        Score counts: {(row.tie_break?.score_counts || [])
                          .map((count, index) => `${index + 1}s: ${count || 0}`)
                          .join(' | ')}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // A cancelled event takes no RSVPs (the server refuses them too), and this page is often
  // reached from an old link, so it says so before anything else. Checked ahead of isPast:
  // "cancelled" is the more useful thing to tell someone than "already happened".
  if (isEventCancelled(event)) {
    return (
      <div className="rsvp-page">
        <div className="rsvp-container">
          <div className="event-info-header">
            <h1>{event.title}</h1>
            <div className="event-details">
              <p className="event-date"><DSIcon name="calendar" /> {formatDate(event.event_date)}</p>
              {event.location && <p className="event-location"><DSIcon name="pin" /> {event.location}</p>}
            </div>
          </div>
          <EventCancelledBanner event={event} />
          <p>RSVPs are closed for this event.</p>
          {existingRsvp && existingRsvp.status !== 'not_going' && (
            <p>You had RSVP'd. There is nothing else you need to do.</p>
          )}
        </div>
      </div>
    );
  }

  if (isPast) {
    return (
      <div className="rsvp-page">
        <div className="rsvp-container">
          <h1>Event Has Passed</h1>
          <p>This event has already occurred. RSVPs are no longer being accepted.</p>
          {renderPingolfLeaderboard()}
        </div>
      </div>
    );
  }

  // For pinball events with MatchPlay, show registration redirect
  if (hasMatchPlay) {
    return (
      <div className="rsvp-page">
        <div className="rsvp-container">
          <div className="event-info-header">
            <h1>{event.title}</h1>
            <div className="event-details">
              <p className="event-date"><DSIcon name="calendar" /> {formatDate(event.event_date)}</p>
              {event.location && <p className="event-location"><DSIcon name="pin" /> {event.location}</p>}
              {event.description && (
                <p className="event-description">{event.description}</p>
              )}
            </div>
          </div>

          <div className="matchplay-registration-section">
            <h2><DSIcon name="gamepad" /> Tournament Registration</h2>
            <p className="matchplay-lede">
              This is an IFPA-sanctioned tournament. Please register on MatchPlay.
            </p>

            {event.event_metadata.checkin_time && (
              <p className="matchplay-detail">
                <strong>Check-in:</strong> {event.event_metadata.checkin_time}
              </p>
            )}

            {event.event_metadata.tournament_fee && (
              <p className="matchplay-detail">
                <strong>Tournament Fee:</strong> ${event.event_metadata.tournament_fee}
              </p>
            )}

            {event.event_metadata.day_pass_fee && (
              <p className="matchplay-detail">
                <strong>Day Pass (non-members):</strong> ${event.event_metadata.day_pass_fee}
              </p>
            )}

            <div className="matchplay-cta">
              <DSButton
                href={event.event_metadata.matchplay_url}
                target="_blank"
                rel="noopener noreferrer"
                appearance="solid"
                tone="primary"
                size="lg"
                trailingIcon={<DSIcon name="arrowRight" />}
              >
                Register on MatchPlay
              </DSButton>
            </div>

            <p className="matchplay-footnote">
              Walk-ins are welcome, but pre-registration is strongly encouraged!
            </p>
          </div>
          {renderPingolfLeaderboard()}
        </div>
      </div>
    );
  }

  // Logged-in user experience - simple and mobile-first
  if (isLoggedIn && currentUser) {
    return (
      <div className="rsvp-page">
        <div className="rsvp-container mobile-optimized">
          <div className="event-info-header">
            <h1>{event.title}</h1>
            <div className="event-details">
              <p className="event-date"><DSIcon name="calendar" /> {formatDate(event.event_date)}</p>
              {event.location && <p className="event-location"><DSIcon name="pin" /> {event.location}</p>}
            </div>
          </div>

          {existingRsvp && (
            <div className="current-rsvp-status">
              <h3>Your Current RSVP</h3>
              <StatusBadge
                variant={
                  existingRsvp.status === 'going'
                    ? 'success'
                    : existingRsvp.status === 'interested'
                      ? 'warning'
                      : 'neutral'
                }
                size="md"
              >
                {existingRsvp.status === 'going' && <><DSIcon name="check" /> Going</>}
                {existingRsvp.status === 'interested' && <><DSIcon name="star" /> Interested</>}
                {existingRsvp.status === 'not_going' && <><DSIcon name="x" /> Not Going</>}
              </StatusBadge>
              {existingRsvp.message && (
                <p className="rsvp-message"><em>"{existingRsvp.message}"</em></p>
              )}
            </div>
          )}

          <div className="rsvp-form-section">
            <h2>{existingRsvp ? 'Update Your RSVP' : 'RSVP to this Event'}</h2>
            
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <form onSubmit={handleLoggedInSubmit}>
              <div className="form-group">
                <span className="rsvp-group-label" id="rsvp-response-label">Your Response *</span>
                {/* Three buttons that pick one of three is a radio group. As plain
                    buttons the choice was invisible to assistive tech: nothing announced
                    which one was selected. */}
                <div className="status-buttons" role="radiogroup" aria-labelledby="rsvp-response-label">
                  {RESPONSE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={formData.status === option.value}
                      className={`status-btn ${formData.status === option.value ? `active ${option.modifier}` : ''}`}
                      onClick={() => setFormData({ ...formData, status: option.value })}
                      disabled={submitting}
                    >
                      <span className="status-btn__icon"><DSIcon name={option.icon} /></span>
                      <span className="label">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <DSTextarea
                  label="Message (Optional)"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Any questions or special requests?"
                  disabled={submitting}
                />
              </div>

              <DSButton
                type="submit"
                appearance="solid"
                tone="primary"
                size="lg"
                className="rsvp-submit"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : (existingRsvp ? 'Update RSVP' : 'Submit RSVP')}
              </DSButton>
            </form>
          </div>
          {renderPingolfLeaderboard()}
        </div>
      </div>
    );
  }

  // Guest user experience - full form
  return (
    <div className="rsvp-page">
      <div className="rsvp-container">
        <div className="event-info-header">
          <h1>{event.title}</h1>
          <div className="event-details">
            <p className="event-date"><DSIcon name="calendar" /> {formatDate(event.event_date)}</p>
            {event.location && <p className="event-location"><DSIcon name="pin" /> {event.location}</p>}
            {event.description && (
              <p className="event-description">{event.description}</p>
            )}
          </div>
        </div>

        <div className="rsvp-form-section">
          <h2>RSVP to this Event</h2>
          
          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleGuestSubmit}>
            <div className="form-row">
              <div className="form-group">
                <DSInput
                  label="First Name *"
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <DSInput
                  label="Last Name *"
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="form-group">
              <DSInput
                label="Email *"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled={submitting}
              />
              <small className="form-hint">
                We'll use this to send you event updates and check-in information
              </small>
            </div>

            <div className="form-group">
              <DSInput
                label="Phone (Optional)"
                type="tel"
                inputMode="tel"
                maxLength={20}
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                disabled={submitting}
              />
            </div>

            <div className="form-group">
              <span className="rsvp-group-label" id="rsvp-guest-status-label">RSVP Status *</span>
              <div className="radio-group" role="radiogroup" aria-labelledby="rsvp-guest-status-label">
                <DSRadio
                  id="rsvp-guest-going"
                  name="status"
                  value="going"
                  label="Going - I'll be there!"
                  checked={formData.status === 'going'}
                  onChange={handleInputChange}
                  disabled={submitting}
                />
                <DSRadio
                  id="rsvp-guest-interested"
                  name="status"
                  value="interested"
                  label="Interested - I might come"
                  checked={formData.status === 'interested'}
                  onChange={handleInputChange}
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="form-group">
              <DSTextarea
                label="Message (Optional)"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows="3"
                placeholder="Any questions or special requests?"
                disabled={submitting}
              />
            </div>

            <div className="privacy-notice">
              <p>
                <strong>Privacy Notice:</strong> Your information will only be used for event 
                management and will not be shared with third parties.
              </p>
            </div>

            <DSButton
              type="submit"
              appearance="solid"
              tone="primary"
              size="lg"
              className="rsvp-submit"
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit RSVP'}
            </DSButton>
          </form>
        </div>
        {renderPingolfLeaderboard()}
      </div>
    </div>
  );
}

export default EventRSVP;
